/**
 * Persistence for the first/last-touch record.
 *
 * Written to BOTH a first-party cookie and localStorage, deliberately:
 *
 *   - **Cookie** is domain-wide (`.naumu.ai`) with a 90-day max-age, so the
 *     record survives the OAuth bounce out to accounts.google.com and back,
 *     and is shared across any subdomain the app is served from. localStorage
 *     is origin-scoped and would not be.
 *   - **localStorage** is the durable copy: it is not sent on every request,
 *     survives cookie clearing by some privacy tools, and holds the record
 *     when the cookie is dropped for being too large.
 *
 * Reads prefer whichever copy is live, and every successful read repairs the
 * other store. All access is defensive — private mode, disabled storage, and
 * corrupted values all degrade to "no record" rather than throwing.
 */

import {
	TOUCH_TTL_MS,
	buildTouchSnapshot,
	isTouchRecordExpired,
	mergeTouchAttribution,
	parseTouchRecord,
	type TouchAttribution,
} from './touch-attribution';

export const TOUCH_COOKIE_NAME = 'naumu-touch';
export const TOUCH_STORAGE_KEY = 'naumu:touch-attribution';

/**
 * Cookie domain. Set to the registrable domain so `naumu.ai`, `www.naumu.ai`
 * and `app.naumu.ai` share one record. Any other host (localhost, preview
 * deployments, the desktop shell's file origin) gets a host-only cookie —
 * setting an explicit domain there would make the browser reject it outright.
 */
const APEX_DOMAIN = 'naumu.ai';

const cookieDomainAttribute = (hostname: string): string => {
	if (hostname === APEX_DOMAIN || hostname.endsWith(`.${APEX_DOMAIN}`)) {
		return `; domain=.${APEX_DOMAIN}`;
	}
	return '';
};

const readCookie = (name: string): string | null => {
	if (typeof document === 'undefined') return null;
	const prefix = `${name}=`;
	for (const part of document.cookie.split('; ')) {
		if (part.startsWith(prefix)) {
			try {
				return decodeURIComponent(part.slice(prefix.length));
			} catch {
				return null;
			}
		}
	}
	return null;
};

/**
 * Per-cookie byte budget. Browsers cap a single cookie at 4096 bytes for the
 * whole `name=value; attrs` string and silently drop anything larger. A record
 * of two fully-populated snapshots at MAX_TOUCH_VALUE_LENGTH serializes to
 * ~5.3KB (more once encodeURIComponent expands non-ASCII), so a hostile or
 * merely very long campaign URL can blow the limit.
 */
const MAX_COOKIE_BYTES = 4000;

const writeCookie = (name: string, value: string, maxAgeMs: number): void => {
	if (typeof document === 'undefined' || typeof window === 'undefined') return;
	const secure = window.location.protocol === 'https:' ? '; secure' : '';
	const attributes =
		`; path=/; max-age=${Math.floor(maxAgeMs / 1000)}; samesite=lax` +
		cookieDomainAttribute(window.location.hostname) +
		secure;
	const cookie = `${name}=${encodeURIComponent(value)}${attributes}`;
	if (cookie.length > MAX_COOKIE_BYTES) {
		// The browser would reject this write and leave whatever is already
		// there, so an older, smaller record could go on shadowing the current
		// one. Expire it instead and let localStorage carry the record.
		document.cookie = `${name}=; path=/; max-age=0${cookieDomainAttribute(
			window.location.hostname,
		)}`;
		return;
	}
	document.cookie = cookie;
};

const readLocalStorage = (key: string): string | null => {
	if (typeof window === 'undefined') return null;
	try {
		return window.localStorage.getItem(key);
	} catch {
		return null;
	}
};

const writeLocalStorage = (key: string, value: string): void => {
	if (typeof window === 'undefined') return;
	try {
		window.localStorage.setItem(key, value);
	} catch {
		// Quota or private mode — the cookie copy still carries the record.
	}
};

/**
 * Read the live record, or null when absent/expired/corrupt.
 *
 * Checks localStorage first (larger, less likely to have been truncated), then
 * the cookie. When only one store has a live record, the other is repaired so
 * the next OAuth bounce still finds it.
 */
export const readTouchRecord = (now: number = Date.now()): TouchAttribution | null => {
	const fromStorage = parseTouchRecord(readLocalStorage(TOUCH_STORAGE_KEY));
	const fromCookie = parseTouchRecord(readCookie(TOUCH_COOKIE_NAME));

	const storageLive = !isTouchRecordExpired(fromStorage, now) ? fromStorage : null;
	const cookieLive = !isTouchRecordExpired(fromCookie, now) ? fromCookie : null;

	const record = storageLive ?? cookieLive;
	if (!record) return null;

	// Repair the store that was missing/stale so both copies agree.
	if (!storageLive || !cookieLive) writeTouchRecord(record, now);
	return record;
};

/** Persist a record to both stores. */
export const writeTouchRecord = (
	record: TouchAttribution,
	now: number = Date.now(),
): void => {
	const serialized = JSON.stringify(record);
	// Renew both stores for the full window rather than the record's residual
	// life — the record itself carries the authoritative `expiresAt`.
	writeCookie(TOUCH_COOKIE_NAME, serialized, Math.max(record.expiresAt - now, TOUCH_TTL_MS));
	writeLocalStorage(TOUCH_STORAGE_KEY, serialized);
};

/**
 * Read the current URL (and, on a document load, the referrer), fold it into
 * the stored record, and persist if anything changed.
 *
 * Returns the live record afterwards — the newly written one, or the untouched
 * existing one when the landing carried no attribution signal. Never throws:
 * attribution must not be able to break a page render.
 *
 * `includeReferrer` should be true only on a real document load. During
 * client-side routing `document.referrer` still points at the original
 * external source, and replaying it would let internal navigation overwrite a
 * gclid last-touch with a stale referrer.
 */
export const recordLanding = (options?: {
	includeReferrer?: boolean;
	now?: number;
}): TouchAttribution | null => {
	if (typeof window === 'undefined') return null;
	try {
		const now = options?.now ?? Date.now();
		const snapshot = buildTouchSnapshot({
			url: window.location.href,
			referrer: options?.includeReferrer ? document.referrer : undefined,
		});
		const existing = readTouchRecord(now);
		const next = mergeTouchAttribution(existing, snapshot, now);
		if (next) {
			writeTouchRecord(next, now);
			return next;
		}
		return existing;
	} catch {
		return null;
	}
};
